import { useEffect, useRef, useState } from 'react';
import { toMajor } from '../../contracts/money';
import { CheckCircle2, Headphones, MapPin, Package, Plus, Users, X } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Drawer } from '../../components/Drawer';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { cn } from '../../lib/cn';
import { useActiveBusiness, useAddOns } from '../../api/hooks';
import { apiPost, apiDelete } from '../../api/client';
import { useQueryClient } from '@tanstack/react-query';

type ActiveAddon = {
  id: string;
  name: string;
  unitPriceMinor: number;
  qty: number;
  used?: number;
  limit?: number;
  unitLabel: string;
  icon: typeof MapPin;
};

type AvailableAddon = {
  id: string;
  name: string;
  unitPriceMinor: number;
  description: string;
  oneTime?: boolean;
};

const ICON_BY_NAME: Record<string, typeof MapPin> = {
  'Extra location': MapPin,
  'Extra user': Users,
  'Priority support': Headphones,
  'SMS pack (1,000)': Headphones,
};

/**
 * Add-ons page — §7.10
 * Lists in-use add-ons with adjust/remove actions and a section for browsing more.
 */
export function AddOns() {
  const { data: business } = useActiveBusiness();
  const { data: addOnData } = useAddOns(business?.id ?? '');
  const qc = useQueryClient();
  const [active, setActive] = useState<ActiveAddon[]>([]);
  const [available, setAvailable] = useState<AvailableAddon[]>([]);
  const [adding, setAdding] = useState<AvailableAddon | null>(null);
  const [removing, setRemoving] = useState<ActiveAddon | null>(null);
  const availableRef = useRef<HTMLDivElement>(null);

  // Initialize from the real subscription add-ons + catalog (mutations below stay
  // local/optimistic — the actual purchase/cancel flow is a follow-on).
  useEffect(() => {
    if (!addOnData) return;
    setActive(addOnData.active.map((a) => ({ id: a.id, name: a.name, unitPriceMinor: a.unitPriceMinor, qty: a.qty, unitLabel: 'unit', icon: ICON_BY_NAME[a.name] ?? Package })));
    setAvailable(addOnData.available.map((a) => ({ id: a.id, name: a.name, unitPriceMinor: a.unitPriceMinor, description: a.description, oneTime: a.oneTime })));
  }, [addOnData]);

  // Optimistic update, then persist + invalidate (the refetch reconciles via the effect above).
  async function handleAdd(addon: AvailableAddon, qty: number) {
    setActive((prev) => [...prev, { id: addon.id, name: addon.name, unitPriceMinor: addon.unitPriceMinor, qty, unitLabel: 'unit', icon: ICON_BY_NAME[addon.name] ?? Package }]);
    setAvailable((prev) => prev.filter((a) => a.id !== addon.id));
    setAdding(null);
    if (business?.id) {
      try {
        await apiPost(`/businesses/${business.id}/add-ons/${addon.id}`);
      } finally {
        void qc.invalidateQueries({ queryKey: ['addons', business.id] });
      }
    }
  }

  async function handleRemove(addon: ActiveAddon) {
    setActive((prev) => prev.filter((a) => a.id !== addon.id));
    setRemoving(null);
    if (business?.id) {
      try {
        await apiDelete(`/businesses/${business.id}/add-ons/${addon.id}`);
      } finally {
        void qc.invalidateQueries({ queryKey: ['addons', business.id] });
      }
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-4xl mx-auto">
      <PageHeader
        eyebrow="Subscription"
        title="Add-ons"
        subtitle="Boost the limits of your plan without changing tiers."
        action={
          <Button
            variant="secondary"
            iconLeft={<Plus className="w-4 h-4" />}
            onClick={() => availableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          >
            Browse more
          </Button>
        }
      />

      <Card className="mb-6 overflow-hidden">
        <div className="px-5 sm:px-6 py-4 border-b border-zinc-200 dark:border-zinc-700 flex items-center justify-between gap-4">
          <h2 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">In use</h2>
          <span className="text-xs text-zinc-500 dark:text-zinc-400">
            {active.length} active
          </span>
        </div>

        {active.length === 0 && (
          <div className="px-5 sm:px-6 py-12 text-center">
            <p className="text-sm text-zinc-500 dark:text-zinc-400">No add-ons yet — browse below.</p>
          </div>
        )}

        <ul className="divide-y divide-zinc-200 dark:divide-zinc-700">
          {active.map((a) => {
            const Icon = a.icon;
            const monthly = a.unitPriceMinor * a.qty;
            return (
              <li key={a.id} className="px-5 sm:px-6 py-4 flex items-start gap-4 flex-wrap">
                <span className="w-10 h-10 grid place-items-center rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 flex-shrink-0">
                  <Icon className="w-5 h-5" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-zinc-950 dark:text-zinc-50">{a.name}</span>
                    <span className="text-xs text-zinc-500 dark:text-zinc-400">×{a.qty}</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-3 text-xs text-zinc-500 dark:text-zinc-400 flex-wrap">
                    <span className="tabular-nums">
                      <Money minor={monthly} hideCents tone="neutral" /> / month
                    </span>
                    {a.used !== undefined && a.limit !== undefined && (
                      <>
                        <span aria-hidden="true">·</span>
                        <span className="tabular-nums">
                          {a.used} / {a.limit} used
                        </span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setRemoving(a)}
                    className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/30 dark:hover:text-rose-400 transition"
                    title="Remove"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </Card>

      <div ref={availableRef}>
        <Card className="overflow-hidden">
          <div className="px-5 sm:px-6 py-4 border-b border-zinc-200 dark:border-zinc-700">
            <h2 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">Available</h2>
            <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
              Compatible with your current plan.
            </p>
          </div>

          {available.length === 0 ? (
            <div className="px-5 sm:px-6 py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">
              You've added every available add-on.
            </div>
          ) : (
            <ul className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {available.map((a) => (
                <li key={a.id} className="px-5 sm:px-6 py-4 flex items-start gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-zinc-950 dark:text-zinc-50">{a.name}</div>
                    <p className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-300">
                      {a.description}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <Money
                      minor={a.unitPriceMinor}
                      hideCents
                      tone="neutral"
                      className="font-medium text-zinc-950 dark:text-zinc-50"
                    />
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">
                      {a.oneTime ? 'one-time' : '/ month'}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    iconLeft={<Plus className="w-3.5 h-3.5" />}
                    onClick={() => setAdding(a)}
                  >
                    Add
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {adding && (
        <AddDrawer
          addon={adding}
          onClose={() => setAdding(null)}
          onConfirm={(qty) => handleAdd(adding, qty)}
        />
      )}
      {removing && (
        <RemoveDrawer
          addon={removing}
          onClose={() => setRemoving(null)}
          onConfirm={() => handleRemove(removing)}
        />
      )}
    </div>
  );
}

function AddDrawer({
  addon,
  onClose,
  onConfirm,
}: {
  addon: AvailableAddon;
  onClose: () => void;
  onConfirm: (qty: number) => void;
}) {
  const [qty, setQty] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const proratedToday = addon.oneTime
    ? addon.unitPriceMinor * qty
    : Math.max(0, qty * addon.unitPriceMinor * (12 / 30));
  const nextMonth = addon.oneTime ? 0 : qty * addon.unitPriceMinor;

  function confirm() {
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setDone(true);
      setTimeout(() => onConfirm(qty), 600);
    }, 600);
  }

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title={done ? 'Added' : `Add — ${addon.name}`}
      width="md"
      footer={
        done ? null : (
          <>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={confirm} loading={submitting}>
              Pay <Money minor={Math.round(proratedToday)} hideCents tone="neutral" className="ml-1 text-white dark:text-zinc-950" />
            </Button>
          </>
        )
      }
    >
      {done ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-950/40 grid place-items-center text-emerald-700 dark:text-emerald-300 mb-4">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">Add-on activated</h3>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
            {addon.name} is live on your subscription.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          <p className="text-sm text-zinc-700 dark:text-zinc-200">{addon.description}</p>

          {!addon.oneTime && (
            <div>
              <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Quantity</label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="w-10 h-10 rounded-md border border-zinc-300 dark:border-zinc-700 grid place-items-center text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
                >
                  −
                </button>
                <input
                  type="number"
                  min={1}
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, parseInt(e.target.value || '1', 10)))}
                  className={cn(
                    'w-24 px-3 py-2.5 text-center text-sm tabular-nums rounded-md',
                    'bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50',
                    'border border-zinc-300 dark:border-zinc-700',
                    'focus:outline-none focus:ring-2 focus:ring-brand dark:focus:ring-brand-dm',
                  )}
                />
                <button
                  type="button"
                  onClick={() => setQty((q) => q + 1)}
                  className="w-10 h-10 rounded-md border border-zinc-300 dark:border-zinc-700 grid place-items-center text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
                >
                  +
                </button>
              </div>
            </div>
          )}

          <div className="rounded-lg border border-zinc-200 dark:border-zinc-600 p-4 space-y-3">
            <div className="text-xs uppercase tracking-wider font-medium text-zinc-500 dark:text-zinc-400">
              {addon.oneTime ? 'Charge today' : 'Effect today (12 days into period)'}
            </div>
            <dl className="text-sm space-y-2">
              <div className="flex items-center justify-between">
                <dt className="text-zinc-600 dark:text-zinc-300">
                  {addon.oneTime
                    ? `${qty} × ৳${toMajor(addon.unitPriceMinor)}`
                    : `Prorated charge today (${qty} × ৳${toMajor(addon.unitPriceMinor)} × 12/30)`}
                </dt>
                <dd className="tabular-nums font-medium text-zinc-950 dark:text-zinc-50">
                  <Money minor={Math.round(proratedToday)} hideCents tone="neutral" />
                </dd>
              </div>
              {!addon.oneTime && (
                <div className="flex items-center justify-between text-xs text-zinc-500 dark:text-zinc-400">
                  <dt>Next month bill ({qty} × ৳{toMajor(addon.unitPriceMinor)})</dt>
                  <dd className="tabular-nums">
                    <Money minor={nextMonth} hideCents tone="neutral" /> <span className="text-[10px]">+ base plan</span>
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      )}
    </Drawer>
  );
}

function RemoveDrawer({
  addon,
  onClose,
  onConfirm,
}: {
  addon: ActiveAddon;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const refundToday = Math.round(Math.max(0, addon.qty * addon.unitPriceMinor * (18 / 30)));

  function confirm() {
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      onConfirm();
    }, 500);
  }

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title={`Remove — ${addon.name}`}
      width="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Keep it</Button>
          <Button variant="destructive" onClick={confirm} loading={submitting}>
            Remove add-on
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-zinc-700 dark:text-zinc-200">
          You'll lose {addon.qty} {addon.unitLabel}{addon.qty === 1 ? '' : 's'} of capacity. We'll credit the unused
          portion to your account toward next invoice.
        </p>
        <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 p-4">
          <div className="text-xs uppercase tracking-wider font-medium text-amber-800 dark:text-amber-300 mb-1">
            Credit on file (estimated)
          </div>
          <div className="tabular-nums font-medium text-zinc-950 dark:text-zinc-50">
            <Money minor={refundToday} hideCents tone="neutral" />
          </div>
        </div>
        {addon.used !== undefined && addon.limit !== undefined && addon.used > addon.limit - addon.qty && (
          <div className="rounded-lg bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 p-4 text-sm text-rose-800 dark:text-rose-300">
            You're using {addon.used} {addon.unitLabel}s — removing this drops your cap to{' '}
            {addon.limit - addon.qty}, which is below current usage. New {addon.unitLabel}s will be blocked
            until usage drops or you re-add.
          </div>
        )}
      </div>
    </Drawer>
  );
}

